import { useNavigate, useSearch } from "@tanstack/react-router";
import { PageHeader } from "./components/PageHeader";
import { NotesPage } from "./NotesPage";
import { ClipsPage } from "./ClipsPage";
import { PlaylistsPage } from "./PlaylistsPage";
import { MatchSummariesPage } from "./MatchSummariesPage";
import type { GoalComposerSource } from "./components/GoalComposerModal";

export type FeedbackLibrarySection =
  | "notes"
  | "observations"
  | "clips"
  | "sessions"
  | "recaps";

const SECTIONS: ReadonlyArray<{
  id: FeedbackLibrarySection;
  label: string;
  description: string;
}> = [
  {
    id: "notes",
    label: "Notes",
    description: "Player-facing teaching points and coach notes.",
  },
  {
    id: "observations",
    label: "Observations",
    description: "Tactical-board and non-video observations.",
  },
  {
    id: "clips",
    label: "Clips",
    description: "Saved video evidence windows.",
  },
  {
    id: "sessions",
    label: "Review Sessions",
    description: "Guided feedback packages for players.",
  },
  {
    id: "recaps",
    label: "Match Recaps",
    description: "Team-level match summaries and linked evidence.",
  },
];

export type FeedbackLibraryPageProps = {
  onCreatePlayerGoal?: (
    playerId: string,
    source: GoalComposerSource,
  ) => void;
};

export function FeedbackLibraryPage({
  onCreatePlayerGoal,
}: FeedbackLibraryPageProps) {
  const navigate = useNavigate({ from: "/coach" });
  const search = useSearch({ from: "/coach" });
  const section = resolveLibrarySection(search.tab, search.library);

  const selectSection = (next: FeedbackLibrarySection) => {
    void navigate({
      to: "/coach",
      search: { tab: "library", library: next },
    });
  };

  return (
    <div className="flex flex-col gap-5" data-testid="feedback-library-page">
      <PageHeader
        title="Feedback Library"
        description="One place to find coach-created feedback objects: notes, observations, clips, review sessions, and match recaps."
      />

      <section
        className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5"
        aria-label="Feedback library sections"
        data-testid="feedback-library-sections"
      >
        {SECTIONS.map((item) => {
          const selected = item.id === section;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => selectSection(item.id)}
              aria-pressed={selected}
              data-testid={`feedback-library-section-${item.id}`}
              className={[
                "rounded border px-3 py-3 text-left transition-colors",
                selected
                  ? "border-accent bg-accent/10 text-ink"
                  : "border-rule bg-bg-card text-ink hover:bg-surface",
              ].join(" ")}
            >
              <span className="block text-sm font-display uppercase tracking-wider">
                {item.label}
              </span>
              <span className="mt-1 block text-xs leading-5 text-ink-muted">
                {item.description}
              </span>
            </button>
          );
        })}
      </section>

      <div data-testid={`feedback-library-active-${section}`}>
        {section === "notes" ? (
          <NotesPage mode="notes" onCreatePlayerGoal={onCreatePlayerGoal} />
        ) : section === "observations" ? (
          <NotesPage
            mode="observations"
            onCreatePlayerGoal={onCreatePlayerGoal}
          />
        ) : section === "clips" ? (
          <ClipsPage onCreatePlayerGoal={onCreatePlayerGoal} />
        ) : section === "sessions" ? (
          <PlaylistsPage />
        ) : (
          <MatchSummariesPage />
        )}
      </div>
    </div>
  );
}

function resolveLibrarySection(
  tab: string | undefined,
  library: FeedbackLibrarySection | undefined,
): FeedbackLibrarySection {
  if (tab === "notes") return "notes";
  if (tab === "clips") return "clips";
  if (tab === "playlists") return "sessions";
  if (tab === "summaries") return "recaps";
  return library ?? "notes";
}
